// @ts-check
import memoize from '@emotion/memoize'
import DOMPurify from 'dompurify'
import { AxiosError } from 'axios'
import { isEmpty } from 'lodash'

export const capitalize = (text: string): string =>
  `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`

export function pluralize(baseStr: string, count: number): string {
  if (count === 1) return baseStr
  return baseStr + 's'
}

export function prettyEnumConstant(role: string): string {
  return role.split('_').map(capitalize).join(' ')
}

/**
 * Unescape HTML entities
 * Use with caution!! React will encode the values again for display to prevent
 * XSS attacks, but if using outside of jsx use caution.
 * @param str
 * @returns {string}
 */
export const unescapeHTMLEntities = memoize((str: string) => {
  const placeholder = document.createElement('textarea')
  placeholder.innerHTML = str

  return placeholder.childNodes.length === 0
    ? ''
    : placeholder.childNodes[0].nodeValue
})

export const sanitize = memoize(str => DOMPurify.sanitize(str))

/**
 * Get error message from api response error
 * @param err Error from response
 * @param fallbackMessage Fallback message if there is no error response
 * @returns {string}
 */
export function messageFromResponseError(
  err: AxiosError,
  fallbackMessage = ''
): string {
  return err?.response?.data && typeof err.response.data === 'string'
    ? err.response.data
    : fallbackMessage
}

/**
 * Validates a url
 * @param {string} url
 * @returns {boolean}
 */
export const urlIsValid = (url: string): boolean => {
  try {
    new URL(url)
  } catch {
    return false
  }
  return true
}

/**
 * strips an array of text from a string
 * @param {string} text
 * @param {Array} stripTextArray
 */
export function stripText(text: string, stripTextArray: string[] = []): string {
  if (isEmpty(stripTextArray)) {
    return text
  }

  let newText = text
  stripTextArray.forEach((textToStrip: string) => {
    newText = newText.replace(textToStrip, '').trim()
  })

  return newText
}

/**
 * escapeStringForPosix
 *
 * Escape string for use in linux based command line
 * https://github.com/jmealo/node-request-to-curl - MIT License
 *
 * @param {string} str
 */
export function escapeStringForPosix(str: string): string {
  function escapeCharacter(x) {
    let code = x.charCodeAt(0)
    if (code < 256) {
      // Add leading zero when needed to not care about the next character.
      return code < 16 ? '\\x0' + code.toString(16) : '\\x' + code.toString(16)
    }
    code = code.toString(16)
    return '\\u' + ('0000' + code).substr(code.length, 4)
  }

  if (/[^\x20-\x7E]|'/.test(str)) {
    // Use ANSI-C quoting syntax.
    return (
      "$'" +
      str
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        .replace(/\n/g, '\\n')
        .replace(/\r/g, '\\r')
        .replace(/[^\x20-\x7E]/g, escapeCharacter) +
      "'"
    )
  } else {
    // Use single quote syntax.
    return "'" + str + "'"
  }
}

export const urlRegex = /((https?):\/\/)?(www.)?[a-z0-9-]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#-]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/gi
export const uuidRegex = /^[A-F\d]{8}-[A-F\d]{4}-[A-F\d]{4}-[89AB][A-F\d]{3}-[A-F\d]{12}$/i

export const isValidId = (maybeId: string): boolean => {
  if (typeof maybeId !== 'string') {
    return false
  }
  return uuidRegex.test(maybeId)
}
